import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Tabs,
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Select,
  Modal,
  Statistic,
  Card,
  Row,
  Col,
  Progress,
  Badge,
  Typography,
  Popconfirm,
  Alert,
  Divider,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  PlusOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileSearchOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  SwapOutlined,
  FileProtectOutlined,
  LockOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import {
  fetchBillRequest,
  clearCurrentBill,
  fetchBillsRequest,
  fetchPendingBillChallansRequest,
  fetchBillStatisticsRequest,
  verifyBillRequest,
  rejectBillRequest,
  closeBillRequest,
  setBillFilters,
} from '../../redux/actions/billReceiptActions';
import BillReceiptForm from './BillReceiptForm';
import BillReceiptDetail, {
  getBillStatusTag,
  getReconStatusTag,
} from './BillReceiptDetail';
import { getPaymentStatusTag } from '../Payment/PaymentPanel';
import './BillReceipt.css';

const { Option } = Select;
const { Text } = Typography;

const PENDING_FETCH_LIMIT = 500;
const PENDING_TABLE_PAGE_SIZE = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (val) =>
  val
    ? new Date(val).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const fmtMoney = (val) =>
  val != null
    ? `₹ ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

// ─── Main Component ───────────────────────────────────────────────────────────

const BillReceipt = () => {
  const dispatch = useDispatch();

  const {
    bills,
    currentBill,
    pendingChallans,
    statistics,
    pagination,
    loading,
    actionLoading,
    filters,
  } = useSelector((state) => state.billReceipts);

  const permissions = useSelector((state) => state.auth.permissions || []);
  const canView   = hasPermission(permissions, 'bill_receipt/view');
  const canCreate = hasPermission(permissions, 'bill_receipt/create');
  const canVerify = hasPermission(permissions, 'bill_receipt/verify');
  const canReject = hasPermission(permissions, 'bill_receipt/reject');
  const canClose  = hasPermission(permissions, 'bill_receipt/close');

  const [activeTab, setActiveTab] = useState('pending');
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);

  // Local filter state (mirrors what's dispatched)
  const [billStatusFilter, setBillStatusFilterLocal] = useState('');
  const [reconStatusFilter, setReconStatusFilterLocal] = useState('');
  const [pendingPage, setPendingPage] = useState(1);
  const [billsPage, setBillsPage] = useState(1);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadPendingChallans = useCallback(
    () => dispatch(fetchPendingBillChallansRequest({ page: 1, limit: PENDING_FETCH_LIMIT })),
    [dispatch]
  );

  const loadBills = useCallback(
    (page = 1, bill_status = billStatusFilter, reconciliation_status = reconStatusFilter) => {
      const params = { page, limit: 10 };
      if (bill_status) params.bill_status = bill_status;
      if (reconciliation_status) params.reconciliation_status = reconciliation_status;
      dispatch(fetchBillsRequest(params));
    },
    [dispatch, billStatusFilter, reconStatusFilter]
  );

  useEffect(() => {
    if (!canView) return;
    dispatch(fetchBillStatisticsRequest());
    loadPendingChallans();
    loadBills(1, '', '');
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleRefresh = () => {
    dispatch(fetchBillStatisticsRequest());
    if (activeTab === 'pending') loadPendingChallans();
    else loadBills(billsPage, billStatusFilter, reconStatusFilter);
  };

  const handleRecordBill = (challan) => {
    setSelectedChallan(challan);
    setIsFormVisible(true);
  };

  const handleViewBill = (bill) => {
    setSelectedBill(bill);
    dispatch(fetchBillRequest(bill.id));
    setIsDetailVisible(true);
  };

  const handleFormSuccess = () => {
    setIsFormVisible(false);
    setSelectedChallan(null);
    loadPendingChallans();
    setPendingPage(1);
    loadBills(1, billStatusFilter, reconStatusFilter);
    dispatch(fetchBillStatisticsRequest());
  };

  const handleBillStatusFilter = (val) => {
    const v = val || '';
    setBillStatusFilterLocal(v);
    setBillsPage(1);
    loadBills(1, v, reconStatusFilter);
  };

  const handleReconStatusFilter = (val) => {
    const v = val || '';
    setReconStatusFilterLocal(v);
    setBillsPage(1);
    loadBills(1, billStatusFilter, v);
  };

  const handleVerify = (bill) => {
    dispatch(verifyBillRequest(bill.id, {}));
  };

  const handleClose = (bill) => {
    dispatch(closeBillRequest(bill.id, {}));
  };

  const handleDetailClose = () => {
    setIsDetailVisible(false);
    setSelectedBill(null);
    dispatch(clearCurrentBill());
    loadBills(billsPage, billStatusFilter, reconStatusFilter);
    loadPendingChallans();
    dispatch(fetchBillStatisticsRequest());
  };

  // ── Statistics helpers ─────────────────────────────────────────────────────

  const totalBillAmount    = statistics?.total_bill_amount ?? 0;
  const pendingChallanCount = statistics?.challans_pending_bill ?? 0;
  const mismatchedBills    = statistics?.mismatched_bills ?? 0;
  const recentBills        = statistics?.recent_bills_30_days ?? 0;
  const billsByStatus      = statistics?.bills_by_status || [];
  const closedCount        = billsByStatus.find((s) => s.bill_status === 'closed')?.count ?? 0;
  const receivedCount      = billsByStatus.find((s) => s.bill_status === 'received')?.count ?? 0;
  const verifiedCount      = billsByStatus.find((s) => s.bill_status === 'verified')?.count ?? 0;
  const rejectedCount      = billsByStatus.find((s) => s.bill_status === 'rejected')?.count ?? 0;

  const groupedPendingChallans = useMemo(() => {
    const byPO = new Map();

    (pendingChallans || []).forEach((challan) => {
      const poId = challan?.purchase_order_id || challan?.purchase_order?.id;
      if (!poId) return;

      const key = `po-${poId}`;
      if (!byPO.has(key)) {
        byPO.set(key, {
          id: key,
          purchase_order_id: poId,
          purchase_order: challan.purchase_order || null,
          vendor_name: challan.purchase_order?.vendor_name || challan.vendor_name || '—',
          challans: [],
        });
      }

      const group = byPO.get(key);
      group.challans.push(challan);
      if (!group.purchase_order && challan.purchase_order) {
        group.purchase_order = challan.purchase_order;
      }
      if (!group.vendor_name || group.vendor_name === '—') {
        group.vendor_name = challan.purchase_order?.vendor_name || challan.vendor_name || '—';
      }
    });

    const toTimestamp = (value) => {
      if (!value) return Number.POSITIVE_INFINITY;
      const ts = new Date(value).getTime();
      return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY;
    };

    return Array.from(byPO.values())
      .map((group) => {
        const sortedChallans = [...group.challans].sort(
          (a, b) => toTimestamp(a.delivery_date) - toTimestamp(b.delivery_date)
        );
        const statusList = Array.from(
          new Set(sortedChallans.map((c) => c.delivery_status).filter(Boolean))
        );

        return {
          ...group,
          challans: sortedChallans,
          challan_numbers: sortedChallans
            .map((c) => c.challan_number)
            .filter(Boolean),
          challan_count: sortedChallans.length,
          earliest_delivery_date: sortedChallans[0]?.delivery_date,
          latest_delivery_date: sortedChallans[sortedChallans.length - 1]?.delivery_date,
          delivery_statuses: statusList,
          partial_count: sortedChallans.filter((c) => c.is_partial_delivery).length,
        };
      })
      .sort((a, b) => Number(b.purchase_order_id || 0) - Number(a.purchase_order_id || 0));
  }, [pendingChallans]);

  const pendingRows = useMemo(() => {
    const start = (pendingPage - 1) * PENDING_TABLE_PAGE_SIZE;
    return groupedPendingChallans.slice(start, start + PENDING_TABLE_PAGE_SIZE);
  }, [groupedPendingChallans, pendingPage]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(groupedPendingChallans.length / PENDING_TABLE_PAGE_SIZE)
    );

    if (pendingPage > maxPage) {
      setPendingPage(maxPage);
    }
  }, [groupedPendingChallans.length, pendingPage]);

  // ── Permission guard ───────────────────────────────────────────────────────

  if (!canView) {
    return (
      <div className="page-container">
        <div className="bill-empty-state">
          <div className="empty-state-icon"><LockOutlined /></div>
          <div className="empty-state-text">
            You don't have permission to view bill receipts.
          </div>
        </div>
      </div>
    );
  }

  // ── Table columns – Pending Challans ──────────────────────────────────────

  const pendingColumns = [
    {
      title: 'PO #',
      dataIndex: 'purchase_order_id',
      key: 'purchase_order_id',
      width: 70,
      render: (id) => <Text strong>#{id}</Text>,
    },
    {
      title: 'Vendor',
      key: 'vendor_name',
      width: 160,
      ellipsis: true,
      render: (_, r) => (
        <Tooltip title={r.vendor_name}>
          <span>{r.vendor_name || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Challans',
      key: 'challan_numbers',
      width: 280,
      render: (_, r) => {
        const challanNumbers = r.challan_numbers || [];
        const preview = challanNumbers.slice(0, 3);
        const remaining = challanNumbers.slice(3);

        return (
          <Space size={[4, 4]} wrap>
            {preview.map((num) => (
              <Tag key={`${r.id}-${num}`} color="blue" style={{ marginInlineEnd: 0 }}>
                {num}
              </Tag>
            ))}
            {remaining.length > 0 && (
              <Tooltip title={remaining.join(', ')}>
                <Tag style={{ marginInlineEnd: 0 }}>+{remaining.length} more</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Count',
      key: 'challan_count',
      width: 80,
      align: 'center',
      render: (_, r) => <Tag color="geekblue">{r.challan_count || 0}</Tag>,
    },
    {
      title: 'Delivery Date',
      key: 'delivery_date',
      width: 130,
      render: (_, r) => {
        const start = r.earliest_delivery_date;
        const end = r.latest_delivery_date;

        if (!start && !end) return '—';
        if (!start || !end || start === end) return fmtDate(end || start);

        return (
          <Tooltip title={`${fmtDate(start)} to ${fmtDate(end)}`}>
            <span>{fmtDate(start)} → {fmtDate(end)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Delivery Status',
      key: 'delivery_status',
      width: 180,
      render: (_, r) => {
        if (!r.delivery_statuses || r.delivery_statuses.length === 0) {
          return '—';
        }

        const map = {
          received:  { color: 'cyan',   text: 'Received'  },
          delivered: { color: 'green',  text: 'Delivered' },
          partial:   { color: 'orange', text: 'Partial'   },
          partially_delivered: { color: 'orange', text: 'Partial' },
        };

        return (
          <Space size={[4, 4]} wrap>
            {r.delivery_statuses.map((status) => {
              const cfg = map[status] || { color: 'default', text: status || '—' };
              return (
                <Tag key={`${r.id}-status-${status}`} color={cfg.color} style={{ marginInlineEnd: 0 }}>
                  {cfg.text}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Partial',
      key: 'is_partial_delivery',
      width: 85,
      align: 'center',
      render: (_, r) =>
        r.partial_count > 0 ? (
          <Tag color="warning" style={{ fontSize: 11 }}>
            {r.partial_count}/{r.challan_count}
          </Tag>
        ) : (
          <Tag style={{ fontSize: 11 }}>No</Tag>
        ),
    },
    {
      title: 'PO Amount',
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (_, r) => fmtMoney(r.purchase_order?.total_amount),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) =>
        canCreate ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={() => handleRecordBill(record)}
          >
            Record Bill
          </Button>
        ) : null,
    },
  ];

  // ── Table columns – All Bills ─────────────────────────────────────────────

  const billColumns = [
    {
      title: 'Bill Number',
      dataIndex: 'bill_number',
      key: 'bill_number',
      width: 110,
      fixed: 'left',
      render: (text) => (
        <Text style={{ color: '#1890ff', fontWeight: 600 }}>{text}</Text>
      ),
    },
    {
      title: 'PO / Vendor / Received By',
      key: 'po_vendor',
      width: 165,
      ellipsis: true,
      render: (_, r) => {
        const vendor = r.purchase_order?.vendor_name || '—';
        return (
          <div style={{ lineHeight: 1.35 }}>
            <div style={{ fontWeight: 600, fontSize: 12 }}>#{r.purchase_order_id}</div>
            <Tooltip title={vendor}>
              <div style={{ color: '#595959', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vendor}</div>
            </Tooltip>
            <Tooltip title={r.received_by_name ? `Received by ${r.received_by_name}` : ''}>
              <div style={{ color: '#bbb', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.received_by_name || '—'}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Date',
      key: 'bill_date',
      width: 80,
      // bill_date is the vendor's invoice date, entered when the bill is
      // recorded. Older bills recorded before that field existed - and any
      // future bill where it's left blank - fall back to received_at (when
      // it entered our system) so the column is never empty.
      render: (_, record) => fmtDate(record.bill_date || record.received_at),
    },
    {
      title: 'Amount',
      dataIndex: 'bill_amount',
      key: 'bill_amount',
      width: 100,
      align: 'right',
      render: fmtMoney,
    },
    {
      title: 'Bill Status',
      dataIndex: 'bill_status',
      key: 'bill_status',
      width: 100,
      render: getBillStatusTag,
    },
    {
      title: 'Recon.',
      dataIndex: 'reconciliation_status',
      key: 'reconciliation_status',
      width: 95,
      render: (status, record) => {
        if (!status || record.bill_status === 'received' || record.bill_status === 'rejected') return '—';
        return getReconStatusTag(status);
      },
    },
    {
      title: 'Payment',
      key: 'payment_status',
      width: 100,
      render: (_, record) => {
        const payStatus = record.payment_status;
        if (!payStatus || record.bill_status === 'rejected') return '—';
        return (
          <Tooltip title="Click View to record or check payments">
            {getPaymentStatusTag(payStatus)}
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="View Details">
            <Button
              type="primary"
              ghost
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewBill(record)}
            />
          </Tooltip>

          {canVerify && record.bill_status === 'received' && (
            <Popconfirm
              title="Verify this bill?"
              description="Moves status: received → verified. Triggers reconciliation."
              onConfirm={() => handleVerify(record)}
              okText="Verify"
              cancelText="Cancel"
            >
              <Tooltip title="Verify">
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  loading={actionLoading}
                />
              </Tooltip>
            </Popconfirm>
          )}

          {canClose && record.bill_status === 'verified' && (
            <Popconfirm
              title={record.reconciliation_status === 'mismatched' ? "Cannot Close Bill" : "Close this bill?"}
              description={record.reconciliation_status === 'mismatched' ? "This bill has reconciliation mismatches. Please open details to assess." : "Final step — cannot be undone. Ensure PO is fully matched."}
              onConfirm={() => record.reconciliation_status === 'mismatched' ? null : handleClose(record)}
              okText={record.reconciliation_status === 'mismatched' ? "Got it" : "Close"}
              cancelText="Cancel"
              okType={record.reconciliation_status === 'mismatched' ? "default" : "danger"}
              showCancel={record.reconciliation_status !== 'mismatched'}
            >
              <Tooltip title={record.reconciliation_status === 'mismatched' ? "Mismatched - Cannot Close" : "Close Bill"}>
                <Button
                  danger={record.reconciliation_status !== 'mismatched'}
                  size="small"
                  icon={<FileDoneOutlined />}
                  loading={actionLoading}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ── Dashboard Tab ─────────────────────────────────────────────────────────

  const DashboardTab = () => {
    const billReconBreakdown = statistics?.bills_by_reconciliation_status || [];
    const totalBills = billsByStatus.reduce((s, x) => s + (x.count || 0), 0);

    return (
      <div>
        {/* Summary stat cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[
            {
              label: 'Received (Pending Verify)',
              value: receivedCount,
              color: '#fa8c16',
              icon: <ClockCircleOutlined />,
            },
            {
              label: 'Verified',
              value: verifiedCount,
              color: '#1890ff',
              icon: <CheckCircleOutlined />,
            },
            {
              label: 'Closed',
              value: closedCount,
              color: '#52c41a',
              icon: <FileDoneOutlined />,
            },
            {
              label: 'Rejected',
              value: rejectedCount,
              color: '#ff4d4f',
              icon: <CloseCircleOutlined />,
            },
          ].map((item) => (
            <Col key={item.label} xs={12} sm={6}>
              <Card size="small" className="bill-stat-card">
                <Statistic
                  title={item.label}
                  value={item.value}
                  prefix={React.cloneElement(item.icon, { style: { color: item.color } })}
                  valueStyle={{ color: item.color, fontSize: 22 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Bills by Status breakdown */}
        {billsByStatus.length > 0 && (
          <Card
            title={<Space><FileDoneOutlined /> Bills by Status</Space>}
            style={{ marginBottom: 24 }}
          >
            <Row gutter={[16, 12]}>
              {billsByStatus.map((item) => {
                const pct = totalBills > 0 ? Math.round((item.count / totalBills) * 100) : 0;
                const colorMap = {
                  received: '#fa8c16',
                  verified: '#1890ff',
                  closed:   '#52c41a',
                  rejected: '#ff4d4f',
                };
                return (
                  <Col key={item.bill_status} xs={24} sm={12} md={6}>
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        {getBillStatusTag(item.bill_status)}
                        <Text strong>{item.count}</Text>
                      </div>
                      <Progress
                        percent={pct}
                        size="small"
                        strokeColor={colorMap[item.bill_status] || '#1890ff'}
                        status={item.bill_status === 'closed' ? 'success' : 'active'}
                      />
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card>
        )}

        {/* Reconciliation breakdown */}
        {billReconBreakdown.length > 0 && (
          <Card
            title={<Space><SwapOutlined /> Reconciliation Status</Space>}
            style={{ marginBottom: 24 }}
          >
            <Row gutter={[16, 12]}>
              {billReconBreakdown.map((item) => {
                const total = billReconBreakdown.reduce((s, x) => s + (x.count || 0), 0);
                const pct   = total > 0 ? Math.round((item.count / total) * 100) : 0;
                const colorMap = { matched: '#52c41a', mismatched: '#ff4d4f', pending: '#fa8c16' };
                return (
                  <Col key={item.reconciliation_status} xs={24} sm={8}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        {getReconStatusTag(item.reconciliation_status)}
                        <Text strong>{item.count}</Text>
                      </div>
                      <Progress
                        percent={pct}
                        size="small"
                        strokeColor={colorMap[item.reconciliation_status]}
                        status={item.reconciliation_status === 'matched' ? 'success' : 'active'}
                      />
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card>
        )}

        {/* PO bill status breakdown */}
        {statistics?.pos_by_bill_status?.length > 0 && (
          <Card title={<Space><FileSearchOutlined /> Purchase Orders by Bill Status</Space>}>
            <Row gutter={[16, 12]}>
              {statistics.pos_by_bill_status.map((item) => {
                const total = statistics.pos_by_bill_status.reduce((s, x) => s + (x.count || 0), 0);
                const pct   = total > 0 ? Math.round((item.count / total) * 100) : 0;
                const colorMap = {
                  pending:           'orange',
                  partially_received:'blue',
                  fully_received:    'cyan',
                  verified:          'geekblue',
                  closed:            'green',
                };
                const strokeMap = {
                  pending:           '#fa8c16',
                  partially_received:'#1890ff',
                  fully_received:    '#13c2c2',
                  verified:          '#2f54eb',
                  closed:            '#52c41a',
                };
                return (
                  <Col key={item.bill_status || Math.random()} xs={24} sm={12} md={6}>
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Tag color={colorMap[item.bill_status] || 'default'}>
                          {(item.bill_status || '').replace(/_/g, ' ')}
                        </Tag>
                        <Text strong>{item.count}</Text>
                      </div>
                      <Progress
                        percent={pct}
                        size="small"
                        strokeColor={strokeMap[item.bill_status] || '#1890ff'}
                      />
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card>
        )}
      </div>
    );
  };

  // ── Tab items ──────────────────────────────────────────────────────────────

  const tabItems = [
    {
      key: 'pending',
      label: (
        <Space>
          <ClockCircleOutlined />
          Pending Bills
          {pendingChallanCount > 0 && (
            <Badge count={pendingChallanCount} size="small" />
          )}
        </Space>
      ),
      children: (
        <div className="bill-table-container">
          <div className="bill-table-header">
            <h2 className="bill-table-title">Challans Awaiting Bill</h2>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadPendingChallans()}
              loading={loading}
            >
              Refresh
            </Button>
          </div>
          <Table
            columns={pendingColumns}
            dataSource={pendingRows}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pendingPage,
              pageSize: PENDING_TABLE_PAGE_SIZE,
              total: groupedPendingChallans.length,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `${range[0]}–${range[1]} of ${total} purchase orders`,
              onChange: (page) => {
                setPendingPage(page);
              },
            }}
            scroll={{ x: 1080 }}
            size="small"
            locale={{
              emptyText: (
                <div className="bill-empty-state">
                  <div className="bill-empty-icon">🎉</div>
                  <div className="bill-empty-text">
                    All challans have bills recorded — nothing pending!
                  </div>
                </div>
              ),
            }}
          />
        </div>
      ),
    },
    {
      key: 'all',
      label: (
        <Space>
          <FileSearchOutlined />
          All Bills
          {mismatchedBills > 0 && (
            <Tooltip title={`${mismatchedBills} mismatched bill(s)`}>
              <Badge count={mismatchedBills} color="orange" size="small" />
            </Tooltip>
          )}
        </Space>
      ),
      children: (
        <div className="bill-table-container">
          <div className="bill-table-header">
            <h2 className="bill-table-title">All Bill Receipts</h2>
            <Space wrap>
              <Select
                placeholder="Filter by bill status"
                style={{ width: 190 }}
                allowClear
                value={billStatusFilter || undefined}
                onChange={handleBillStatusFilter}
              >
                <Option value="received">🟡 Received</Option>
                <Option value="verified">🔵 Verified</Option>
                <Option value="closed">🟢 Closed</Option>
                <Option value="rejected">🔴 Rejected</Option>
              </Select>
              <Select
                placeholder="Filter by reconciliation"
                style={{ width: 200 }}
                allowClear
                value={reconStatusFilter || undefined}
                onChange={handleReconStatusFilter}
              >
                <Option value="matched">✅ Matched</Option>
                <Option value="mismatched">⚠️ Mismatched</Option>
                <Option value="pending">⏳ Pending</Option>
              </Select>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadBills(billsPage, billStatusFilter, reconStatusFilter)}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </div>

          {mismatchedBills > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message={`${mismatchedBills} bill(s) have reconciliation mismatches. Review them to ensure billing accuracy.`}
              style={{ marginBottom: 16 }}
              closable
            />
          )}

          <Table
            columns={billColumns}
            dataSource={bills}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 840 }}
            rowClassName={(record) =>
              record.reconciliation_status === 'mismatched' ? 'bill-row-mismatched' : ''
            }
            pagination={{
              current: billsPage,
              pageSize: 10,
              total: pagination?.total || 0,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `${range[0]}–${range[1]} of ${total} bills`,
              onChange: (page) => {
                setBillsPage(page);
                loadBills(page, billStatusFilter, reconStatusFilter);
              },
            }}
            scroll={{ x: 1400 }}
            size="small"
            locale={{
              emptyText: (
                <div className="bill-empty-state">
                  <div className="bill-empty-icon"><FileProtectOutlined /></div>
                  <div className="bill-empty-text">No bill receipts found.</div>
                </div>
              ),
            }}
          />
        </div>
      ),
    },
    {
      key: 'dashboard',
      label: (
        <Space>
          <DollarOutlined />
          Dashboard
        </Space>
      ),
      children: <DashboardTab />,
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><FileProtectOutlined /></div>
            <div className="page-header-text">
              <h1>Bill Receipts</h1>
              <p>Record, verify, reject and close vendor bills for delivered challans</p>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="stats-grid">
          <div className="stat-card" style={{ borderLeftColor: '#fa8c16' }}>
            <div className="stat-icon" style={{ background: '#fff7e6', color: '#fa8c16' }}><ClockCircleOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#fa8c16' }}>{pendingChallanCount}</div>
              <div className="stat-label">Pending Bill</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#1890ff' }}>
            <div className="stat-icon" style={{ background: '#e6f4ff', color: '#1890ff' }}><SearchOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#1890ff' }}>{receivedCount}</div>
              <div className="stat-label">Awaiting Verification</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: mismatchedBills > 0 ? '#fa8c16' : '#52c41a' }}>
            <div className="stat-icon" style={{ background: mismatchedBills > 0 ? '#fff7e6' : '#f6ffed', color: mismatchedBills > 0 ? '#fa8c16' : '#52c41a' }}><WarningOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: mismatchedBills > 0 ? '#fa8c16' : '#52c41a' }}>{mismatchedBills}</div>
              <div className="stat-label">Mismatched</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#722ed1' }}>
            <div className="stat-icon" style={{ background: '#f9f0ff', color: '#722ed1' }}><DollarOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#722ed1' }}>
                ₹{Number(totalBillAmount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
              <div className="stat-label">Total Amount</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          className="bill-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabBarExtraContent={
            activeTab !== 'dashboard' && (
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                Refresh
              </Button>
            )
          }
        />
      </div>

      {/* Record Bill Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            Record Bill Receipt
          </Space>
        }
        open={isFormVisible}
        onCancel={() => {
          setIsFormVisible(false);
          setSelectedChallan(null);
        }}
        footer={null}
        width={860}
        destroyOnClose
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <BillReceiptForm
          challan={selectedChallan}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setIsFormVisible(false);
            setSelectedChallan(null);
          }}
        />
      </Modal>

      {/* Bill Detail Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Bill Receipt Details
            {selectedBill && getBillStatusTag(selectedBill.bill_status)}
          </Space>
        }
        open={isDetailVisible}
        onCancel={handleDetailClose}
        footer={<Button onClick={handleDetailClose}>Close</Button>}
        width={860}
        destroyOnClose
      >
        <BillReceiptDetail bill={currentBill || selectedBill} loading={loading} onClose={handleDetailClose} />
      </Modal>
    </div>
  );
};

export default BillReceipt;
